import type { ProfileReviewStatus } from "@shared/types";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { formatRupees } from "@/lib/format";

const NOT_PROVIDED = "Not provided";

/**
 * The designer profile fields the Users page detail cast needs. Kept local
 * rather than added to `packages/shared-types` -- the backend already
 * returns the full `designerProfiles` row on the user detail endpoint, and
 * this mirrors the same local-cast approach `realtorProfileOf` uses in
 * users-dashboard.tsx for the same reason: the shared `AdminUserRecord.profile`
 * type stays intentionally narrow, so callers that need more cast to a
 * shape matching what the endpoint is known to send.
 */
export interface ReviewableDesignerProfile {
	id: string;
	firmName?: string | null;
	bio?: string | null;
	services?: string[] | null;
	styles?: string[] | null;
	serviceCities?: string[] | null;
	priceRangeMin?: number | null;
	priceRangeMax?: number | null;
	experienceYears?: number | null;
	projectsCompleted?: number | null;
	portfolioImages?: string[] | null;
	isVerified: boolean;
	reviewStatus: ProfileReviewStatus;
	reviewNotes?: string | null;
}

function DetailField({
	label,
	children,
}: {
	label: string;
	children: ReactNode;
}) {
	return (
		<div className="grid gap-1">
			<span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
				{label}
			</span>
			{children}
		</div>
	);
}

function textOrNotProvided(value: string | null | undefined): string {
	return value?.trim() || NOT_PROVIDED;
}

function listOrNotProvided(values: string[] | null | undefined): string {
	return values?.length ? values.join(", ") : NOT_PROVIDED;
}

function yearsOrNotProvided(years: number | null | undefined): string {
	if (years === null || years === undefined) return NOT_PROVIDED;
	return years === 1 ? "1 year" : `${years} years`;
}

function priceRangeOrNotProvided(
	min: number | null | undefined,
	max: number | null | undefined,
): string {
	if (min === null || min === undefined || max === null || max === undefined) {
		return NOT_PROVIDED;
	}
	return `${formatRupees(min)} – ${formatRupees(max)}`;
}

/** A count, never the URLs themselves -- these files are only meaningful to open, not to list. */
function imageCount(values: string[] | null | undefined, noun: string): string {
	const count = values?.length ?? 0;
	return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

function ReviewStatusBadge({ status }: { status: ProfileReviewStatus }) {
	const variant =
		status === "approved"
			? "success"
			: status === "rejected"
				? "danger"
				: "warning";
	return <Badge variant={variant}>{status}</Badge>;
}

/**
 * A designer's firm, services and portfolio summary, shown in the Users page
 * detail sheet the same way a realtor's profile is -- the backend already
 * returns the full `designerProfiles` row on the user detail endpoint.
 */
export function DesignerReviewDetails({
	profile,
}: {
	profile: ReviewableDesignerProfile;
}) {
	return (
		<Card className="border-slate-200 shadow-none">
			<CardHeader>
				<CardTitle>Designer details</CardTitle>
				<CardDescription>
					The firm, services and portfolio summary from the designer's profile.
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-4 text-sm">
				<DetailField label="Firm name">
					<span className="font-medium">
						{textOrNotProvided(profile.firmName)}
					</span>
				</DetailField>
				<DetailField label="Bio">
					<span>{textOrNotProvided(profile.bio)}</span>
				</DetailField>
				<DetailField label="Services">
					<span>{listOrNotProvided(profile.services)}</span>
				</DetailField>
				<DetailField label="Styles">
					<span>{listOrNotProvided(profile.styles)}</span>
				</DetailField>
				<DetailField label="Service cities">
					<span>{listOrNotProvided(profile.serviceCities)}</span>
				</DetailField>
				<DetailField label="Price range">
					<span>
						{priceRangeOrNotProvided(
							profile.priceRangeMin,
							profile.priceRangeMax,
						)}
					</span>
				</DetailField>
				<DetailField label="Experience">
					<span>{yearsOrNotProvided(profile.experienceYears)}</span>
				</DetailField>
				<DetailField label="Projects completed">
					<span>{profile.projectsCompleted ?? 0}</span>
				</DetailField>
				<DetailField label="Portfolio">
					<span>{imageCount(profile.portfolioImages, "image")}</span>
				</DetailField>
				<DetailField label="Verification">
					<div className="flex flex-wrap gap-2">
						<Badge variant={profile.isVerified ? "success" : "warning"}>
							{profile.isVerified ? "Verified" : "Not verified"}
						</Badge>
						<ReviewStatusBadge status={profile.reviewStatus} />
					</div>
				</DetailField>
				<DetailField label="Review notes">
					<span>{textOrNotProvided(profile.reviewNotes)}</span>
				</DetailField>
			</CardContent>
		</Card>
	);
}
